import { useEffect, useMemo } from 'react';
import { Form, FormInstance, TableColumnType } from 'antd';
import { useTranslation } from 'react-i18next';

import { Rule } from 'types';
import { CompensationType, Namespace } from 'enums';
import { EditableCheckbox, EditableNumber } from 'components/common';

export const useRulesTableColumns = (
  form: FormInstance,
  loading: boolean,
  isEditing: (rule: Rule) => boolean,
  actionsColumn: TableColumnType<Rule>,
) => {
  const { t } = useTranslation(Namespace.Settings);
  const isSelectedFixedAmount = Form.useWatch('fixedAmount', form);

  useEffect(() => {
    return isSelectedFixedAmount ? form.setFieldsValue({ amountLimit: null }) : form.setFieldsValue({ amount: null });
  }, [isSelectedFixedAmount, form]);

  return useMemo<TableColumnType<Rule>[]>(() => {
    return [
      {
        title: t('compensationType'),
        dataIndex: 'compensationType',
        render: (type: CompensationType) => t(type, { ns: Namespace.CompensationTypes }),
      },
      {
        title: t('requiresBill'),
        dataIndex: 'requiresBill',
        align: 'center',
        render: (requiresBill: boolean, rule: Rule) => (
          <EditableCheckbox
            name="requiresBill"
            editing={isEditing(rule)}
            checked={requiresBill}
            disabled={loading}
            emptyNoSelected
          />
        ),
      },
      {
        title: t('fixedAmount'),
        dataIndex: 'fixedAmount',
        align: 'center',
        render: (fixedAmount: boolean, rule: Rule) => (
          <EditableCheckbox
            name="fixedAmount"
            editing={isEditing(rule)}
            checked={fixedAmount}
            disabled={loading}
            emptyNoSelected
          />
        ),
      },
      {
        title: `${t('amount')}, $`,
        dataIndex: 'amount',
        align: 'right',
        render: (amount: number, rule: Rule) => (
          <EditableNumber
            name="amount"
            value={amount}
            required={isSelectedFixedAmount}
            editing={isEditing(rule)}
            disabled={loading || !isSelectedFixedAmount}
            min={1}
          />
        ),
      },
      {
        title: t('amountPercent'),
        dataIndex: 'amountPercent',
        align: 'right',
        render: (amountPercent: number, rule: Rule) => (
          <EditableNumber
            name="amountPercent"
            value={amountPercent}
            editing={isEditing(rule)}
            disabled={loading}
            min={1}
          />
        ),
      },
      {
        title: `${t('amountLimit')}, $`,
        dataIndex: 'amountLimit',
        align: 'right',
        render: (amountLimit: number, rule: Rule) => (
          <EditableNumber
            name="amountLimit"
            value={amountLimit}
            editing={isEditing(rule)}
            disabled={loading || isSelectedFixedAmount}
            min={1}
          />
        ),
      },
      actionsColumn,
    ];
  }, [t, actionsColumn, isEditing, loading, isSelectedFixedAmount]);
};
